import { TypeChecker } from '@attain-apps/type-guards'

import * as edge from '../edge'

import type { Context } from './context'
import type { ForRecords } from './for-records'
import type { ForValue } from './for-value'
import { Node } from './node'

/**
 * Represents the combined results of a collection of {@link Node}
 *
 * @see {@link ForGroup.Member}
 */
export class ForGroup<out T = unknown> extends Node {
  public readonly members: readonly ForGroup.Member<T>[]

  protected override readonly connectionTraversalStartingNodes: readonly Node[]

  constructor(params: ForGroup.constructor.Params<T>) {
    super(params)
    this.members = params.members
    this.connectionTraversalStartingNodes = this.members

    // nodes get consumed by the group
    this.edges.consuming.add(
      ...this.members
        .filter(
          // NOTE: Casting is required to tell TypeScript that this type
          // checker is checking for a subset of the group member type,
          // which is needed to get the right overload of `filter`
          edge.ForConsumption.isValidNode,
        )
        // TODO: determine if the edge is required or not, this will be affected
        // by filters.
        .map<edge.ForConsumption>(node => ({
          isRequired: false,
          node,
          // When consuming the member nodes, we always want the full list.
          // Whether or not this node is consumed as a singleton is
          // determined elsewhere
          resultsAsSingleton: false,
        })),
    )
  }
}
export namespace ForGroup {
  export const isForGroup: TypeChecker<ForGroup> = TypeChecker.Factory.instance(
    ForGroup,
  ).usingAlias('query.graph.node.ForGroup')

  export namespace constructor {
    export interface Params<T = unknown>
      extends Node.constructor.Params,
        Pick<Context, 'schema'> {
      members: readonly ForGroup.Member<T>[]
    }
  }
  export type Member<T = unknown> =
    | ForRecords<T>
    | ForValue<Maybe.ReadonlyArray<T>>
}
